import { MODERATION_ERROR_CODES } from '@aysar/contracts';

import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

/** What the reports surface should say about a refusal, and what to offer next. */
export interface ModerationErrorCopy {
  /** A `moderation:error.*` key. */
  i18nKey: string;
  /**
   * A colleague decided between this reviewer opening the report and submitting
   * (409).
   *
   * The queue polls and two reviewers on one row is the **ordinary** case, not a
   * fault — the report was genuinely undecided when it was opened. So the answer
   * is **re-read**, never retry: the dialog offers a refresh, and the verdict on
   * screen is somebody else's to keep.
   */
  isConflict: boolean;
  /**
   * The report is not there any more (404).
   *
   * Distinct from a conflict, because there is nothing to go and read: the
   * dialog closes and the queue refreshes rather than leaving an operator
   * looking at a row that no longer exists.
   */
  isGone: boolean;
}

/**
 * The refusals this surface can meet, and the key that answers each.
 *
 * **Only two, deliberately.** The other three `MODERATION_ERROR_CODES` belong to
 * the member's own routes (`POST /moderation/reports`, `/blocks`) — blocking
 * yourself, a target that is gone, a conversation ref that is not yours. No
 * staff route can produce them, so mapping them here would be copy nothing could
 * ever show, and the orphan check is not what would catch that.
 *
 * Codes come from the contracts constants rather than string literals (U3): they
 * are the same objects the api's `DomainError` subclasses read, so a rename is a
 * compile error instead of a silent fallback to "something went wrong".
 */
const KEY_BY_CODE: Readonly<Record<string, string>> = {
  [MODERATION_ERROR_CODES.reportAlreadyResolved]: 'error.alreadyResolved',
  [MODERATION_ERROR_CODES.reportNotFound]: 'error.notFound',
};

/** Turns an api refusal into what this surface says about it, or `null`. */
export function moderationErrorCopy(
  error: ApiError | null | undefined,
): ModerationErrorCopy | null {
  if (error === null || error === undefined) {
    return null;
  }

  const i18nKey = KEY_BY_CODE[error.code];

  if (i18nKey === undefined) {
    return null;
  }

  return {
    i18nKey,
    isConflict: error.code === MODERATION_ERROR_CODES.reportAlreadyResolved,
    isGone: error.code === MODERATION_ERROR_CODES.reportNotFound,
  };
}
